// src/admin/screens/actions/ActionRequestsScreen.tsx
// Super-admin review queue for admin_action_requests rows. Any non-pending
// state is filterable for audit history; Review action is only offered on
// pending rows and gated on actions.review (super_admin, non-readonly).
//
// Self-review is blocked server-side in the admin_action_review RPC — the
// UI also hides the Review button when the viewer is the original requester.
import { useMemo, useState } from 'react';
import Button from '@admin/components/Button';
import DataTable, { type Column } from '@admin/components/DataTable';
import FilterChip from '@admin/components/FilterChip';
import SectionHead from '@admin/components/SectionHead';
import {
  useActionRequests,
  type ActionRequestRow,
  type ActionStatus,
} from '@admin/hooks/useActionRequests';
import { useRole } from '@admin/auth/useRole';
import { useAuth } from '@admin/auth/useAuth';
import { formatCatDateTime } from '@admin/utils/dates';
import ReviewActionModal from './ReviewActionModal';
import styles from './ActionRequestsScreen.module.css';

const TABS: { key: ActionStatus; label: string }[] = [
  { key: 'pending', label: 'Pending' },
  { key: 'approved', label: 'Approved' },
  { key: 'rejected', label: 'Rejected' },
  { key: 'superseded', label: 'Superseded' },
];

function shortId(id: string): string {
  return id.slice(0, 8);
}

function formatTarget(r: ActionRequestRow): string {
  if (r.target_loan_id) return `loan:${shortId(r.target_loan_id)}`;
  if (r.target_borrower_id) return `borrower:${shortId(r.target_borrower_id)}`;
  if (r.target_payment_id) return `payment:${shortId(r.target_payment_id)}`;
  return '—';
}

export default function ActionRequestsScreen() {
  const { can } = useRole();
  const { admin: me } = useAuth();
  const [tab, setTab] = useState<ActionStatus>('pending');
  const { data, isLoading, error, refetch } = useActionRequests([tab]);
  const [reviewing, setReviewing] = useState<ActionRequestRow | null>(null);
  const rowCount = data?.length ?? 0;

  const columns = useMemo<Column<ActionRequestRow>[]>(
    () => [
      {
        key: 'type',
        header: 'Action',
        render: (r) => <code className={styles.code}>{r.action_type}</code>,
      },
      {
        key: 'target',
        header: 'Target',
        render: (r) => (
          <span className="mono" style={{ fontSize: 12.5, color: 'var(--c-text-sec)' }}>
            {formatTarget(r)}
          </span>
        ),
      },
      {
        key: 'by',
        header: 'Requested by',
        render: (r) => (
          <span style={{ fontWeight: 500 }}>{r.requested_by?.display_name ?? r.requested_by?.email ?? '—'}</span>
        ),
      },
      {
        key: 'when',
        header: 'When',
        render: (r) => (
          <span className={styles.muted}>{formatCatDateTime(r.requested_at)}</span>
        ),
      },
      {
        key: 'notes',
        header: 'Notes',
        render: (r) => <span className={styles.notes}>{r.request_notes ?? <span style={{ color: 'var(--c-text-dim)' }}>—</span>}</span>,
      },
      {
        key: 'actions',
        header: '',
        align: 'right',
        render: (r) => {
          if (r.status !== 'pending') {
            return (
              <span className={styles.muted}>
                {r.reviewed_by?.display_name ?? r.reviewed_by?.email ?? ''}
              </span>
            );
          }
          if (!can('actions.review')) return null;
          if (me?.id === r.requested_by_id) {
            return <span className={styles.muted}>your request</span>;
          }
          return (
            <Button size="sm" onClick={() => setReviewing(r)}>
              Review
            </Button>
          );
        },
      },
    ],
    [can, me?.id],
  );

  const closeWith = (refresh: boolean) => {
    setReviewing(null);
    if (refresh) void refetch();
  };

  const tabLabel = tab === 'pending' ? 'awaiting review' : tab;

  return (
    <div className={styles.page}>
      <SectionHead
        eyebrow="Approvals"
        title="Action requests"
        description={`${rowCount} ${tabLabel} request${rowCount === 1 ? '' : 's'} · super-admin review only`}
      />

      <div className={styles.tabs}>
        {TABS.map((t) => (
          <FilterChip
            key={t.key}
            label={t.label}
            active={tab === t.key}
            onClick={() => setTab(t.key)}
          />
        ))}
      </div>

      <DataTable<ActionRequestRow>
        rows={data ?? []}
        columns={columns}
        isLoading={isLoading}
        error={error ? (error as Error).message : null}
        emptyLabel={tab === 'pending' ? 'No pending requests.' : `No ${tab} requests.`}
        rowKey={(r) => r.id}
      />

      {reviewing && <ReviewActionModal req={reviewing} onClose={closeWith} />}
    </div>
  );
}
